import { Component, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Observable } from 'rxjs/Observable';

import { AuthService } from '../services/auth.service';
import { DvlSettingsService } from '../services/settings.service';
import { DvlBirthdaysService } from '../services/birthdays.service';
import { DvlBraceletColorPipe } from '../pipes/bracelet-color.pipe';
import { DvlFoodPipe } from '../pipes/food.pipe';
import { DvlDessertPipe } from '../pipes/dessert.pipe';
import { Birthday } from '../models/birthday.model';
import jsPDF from 'jspdf';
import * as pdfHeaderURL from '../../assets/img/pdf/pdf-cabecera.png';
import * as pdfFooterURL from '../../assets/img/pdf/pdf-pie.png';
import { Base64 } from 'js-base64';

@Component({
  selector: 'dvl-monitor',
  templateUrl: './monitor.component.html'
})
export class DvlMonitorComponent {
  @ViewChild('attendeeMonitorForm') attendeeForm: any;
  private showInfoForm: boolean;
  private date: string;
  private turnSelected: string;
  private birthdayFocus: any;
  private birthdays: any[];
  private form: FormGroup;
  private bracelets: any[];
  private attendees: any[];
  private desserts: any[];
  private foods: any[];
  private message: {};
  private showBraceletPicker: boolean;

  private timetable$: Observable<any[]>;

  constructor(
    private settingsService: DvlSettingsService,
    private birthdaysService: DvlBirthdaysService,
    private formBuilder: FormBuilder,
    private braceletPipe: DvlBraceletColorPipe,
    private foodPipe: DvlFoodPipe,
    private dessertPipe: DvlDessertPipe,
    private router: Router,
    private authService: AuthService
  ) {
    let date: any[] = this.birthdaysService.selectedData.split('-');

    this.birthdayFocus = new Birthday({});
    this.timetable$ = this.settingsService.timetable$;
    this.attendees = [];

    this.date = `${date[2]}/${date[1]}/${date[0]}`;

    this.settingsService.foods$.subscribe((foods) => this.foods = foods);
    this.settingsService.desserts$.subscribe((desserts) => this.desserts = desserts.filter((dessert) => dessert.active));
    this.settingsService.bracelets$.subscribe((bracelets) => this.bracelets = bracelets.filter((bracelet) => bracelet.active));
    this.birthdaysService.birthdays$.subscribe((birthdays) => {
      this.birthdays = birthdays;
      this.setBirthday();
    });
    this.timetable$.subscribe((timetable) => this.selectHour(timetable[0].hour));

    this.form = this.formBuilder.group({
      'name': [undefined],
      'age': [undefined],
      'bracelet': [undefined, Validators.required],
      'dessert': [undefined]
    });

    this.form.valueChanges.subscribe((values) => {
      if (this.form.valid && this.turnSelected !== undefined) {
        this.save(values);
      }
    });

    this.authService.isLogged.subscribe((value) => {
      if (!value) {
        this.router.navigate(['/']);
      }
    });
  }

  private onListSubmit(attendee: any, index?: number): void {
    this.attendees = this.attendees.map((el) => Object.assign({}, el)) || [];

    if (index !== undefined) {
      this.attendees[index] = attendee;
    } else {
      this.attendees.push(attendee);
    }

    if (this.birthdayFocus.sentToKitchen) {
      this.submit(false);
    } else {
      this.save(this.birthdayFocus);
    }
  }

  private setBirthdayNote(note: string): void {
    if (this.birthdayFocus) {
      this.birthdayFocus.note = note;
      this.save(this.birthdayFocus);
    }
  }

  private setBirthday(): void {
    if (this.birthdays && this.turnSelected) {
      this.birthdayFocus = this.birthdays.find((birthday) => birthday.turn === this.turnSelected) || new Birthday({});
    }

    this.attendees = (this.birthdayFocus && this.birthdayFocus.attendees) ? this.birthdayFocus.attendees.map((attendee) => Object.assign({}, attendee)) : [];
  }

  private selectHour(hour: string): void {
    this.turnSelected = hour;
    this.setBirthday();
    this.setForm();
    this.attendeeForm.clear();
  }

  private setForm(): void {
    if (this.form) {
      this.form.reset();
      this.form.patchValue(this.birthdayFocus);
    }
  }

  private getBraceletColor(turn: number): string {
    const birthday: any = this.birthdays.find((el) => el.turn === turn);
    return this.braceletPipe.transform(birthday ? birthday.bracelet : '', this.bracelets);
  }

  private onSend(): void {
    if (!this.birthdayFocus.bracelet) {
      this.setMessage({
        error: true,
        popup: true,
        text: 'Escoja una pulsera para el cumpleaños.'
      });
      this.submit(false);
    } else {
      this.submit(true);
    }
  }

  private errorAttendee(message: string): void {
    this.setMessage({
      error: true,
      text: message,
      popup: false
    });
  }

  private submit(sentToKitchen: boolean): void {
    const isFullFilled: boolean = this.attendees.reduce((acc, attendee) => acc && !!attendee.food, true);
    let birthday: any = this.form.value;

    if (!isFullFilled && !birthday.sentToKitchen) {
      this.setMessage({
        error: true,
        text: "Hay niños en la comanda sin comida!",
        popup: true
      });
    }

    if (isFullFilled && sentToKitchen) {
      birthday.sentToKitchen = true;
    }

    birthday.attendees = this.attendees;
    birthday.updates = this.birthdayFocus.updates || [];
    birthday.updates = birthday.updates.concat(this.getUpdates(birthday));
    birthday.updates = birthday.updates.sort((a, b) => {
      const aDate: number = new Date(a.hour).getTime();
      const bDate: number = new Date(b.hour).getTime();

      if (a.read || aDate < bDate) {
        return 1;
      } else if (aDate > bDate) {
        return -1;
      } else {
        return 0;
      }
    });

    this.save(birthday);
  }

  private setMessage(message: any): void {
    this.message = message;
    // Comment this line to do not hide the message
    setTimeout(() => this.message = undefined, 3000);
  }

  private save(birthday: any): void {
    birthday.turn = this.turnSelected;
    birthday.attendees = this.attendees.map((attendee) => Object.assign({}, attendee));
    birthday.sentToKitchen = (birthday.sentToKitchen !== undefined) ? birthday.sentToKitchen : this.birthdayFocus.sentToKitchen;

    /* this.birthdaysService.saveBirthday(birthday).then((message) => {
      if (birthday.bracelet && birthday.sentToKitchen) {
        message.popup = false;
        this.setMessage(message);
      }
    }); */
  }

  private getUpdates(birthday: any): any[] {
    const date: Date = new Date();
    const prevAttendees: any[] = this.birthdayFocus.attendees.map((attendee) => Object.assign({}, attendee)) || [];
    const currAttenddess: any[] = birthday.attendees.map((attendee) => Object.assign({}, attendee)) || [];
    let foodsDiff: any[] = [];
    let notesUpdates: any[] = [];

    foodsDiff = this.foods.map((food) => {
      return {
        read: false,
        hour: date,
        food: food.$key,
        text: 'Nueva cantidad',
        dif: currAttenddess.filter((el) => el.food === food.$key).length - prevAttendees.filter((el) => el.food === food.$key).length
      };
    }).filter((update) => update.dif !== 0);

    notesUpdates = this.foods.map((food) => {
      let prevNotes: any[] = prevAttendees.filter((el) => el.food === food.$key && el.note).map((attendee) => attendee.note);

      return {
        food: food.$key,
        newNotes: currAttenddess.filter((el) => el.food === food.$key && el.note && el.note.length > 0).map((attendee) => attendee.note).filter((note) => note.length > 0 && prevNotes.indexOf(note) === -1)
      };
    }).filter((update) => update.newNotes.length > 0)
      .reduce((acc, curr) => acc.concat(curr.newNotes.map((note) => {
        return {
          food: curr.food,
          note: note,
          read: false,
          hour: date,
          text: 'Nueva nota'
        };
      })), []);


    return foodsDiff.concat(notesUpdates);
  }

  private selectBracelet(braceletKey: any): void {
    this.showBraceletPicker = !this.showBraceletPicker;
    this.form.controls['bracelet'].setValue(braceletKey);
  }

  private print(): void {
    let doc: any = new jsPDF();
    let textListAttendee: string = '';

    const imgHeader = new Image();
    const imgFooter = new Image();

    imgHeader.src = pdfHeaderURL;
    imgHeader.onload = () => {
      doc.addImage(imgHeader, 'png', 0, 0, 210, 30);
      doc.setFontSize(16);
      doc.setTextColor(147, 17, 126);
      doc.setFontType("bold");
      doc.text(20, 40, 'Fiesta de:');
      doc.setFontType("normal");
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(15);
      // doc.text(20, 50, this.birthdayFocus.name + ', de ' + this.birthdayFocus.age + ' años.');
      doc.text(20, 50, `${ this.birthdayFocus.name } disfrutando de sus ${this.birthdayFocus.age} años.`);
      doc.text(20, 60, `En Diverland el día ${ this.date } a las ${this.birthdayFocus.turn}`);
      doc.text(20, 70, `Postre: ${this.dessertPipe.transform(this.birthdayFocus.dessert, this.desserts) || 'Sin postre'}`);
      doc.setFontSize(16);
      doc.setTextColor(147, 17, 126);
      doc.setFontType("bold");
      doc.text(20, 90, 'Invitados a la fiesta: (' + this.attendees.length + ')');
      doc.setFontType("normal");
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(13);

      // Mostrando los invitados en parrafo para que quepan
      this.attendees.forEach((attendee, i) => {
        // doc.text(20, 10 + (i * 7) + 67, `Nombre: ${(attendee.name || 'Sin nombre')}. Comida: ${this.foodPipe.transform(attendee.food, this.foods)}`);
        textListAttendee += (i + 1) + '- ' + attendee.name + ',  ';
      });
      var lMargin=20; //left margin in mm
      var rMargin=20; //right margin in mm
      var pdfInMM=210;  // width of A4 in mm
      var pageCenter=20;
      var lines =doc.splitTextToSize(textListAttendee, (pdfInMM-lMargin-rMargin));
      var dim = doc.getTextDimensions('Text');
      var lineHeight = dim.h;
      for(var i=0;i<lines.length;i++){
        let lineTop = (lineHeight/2)*i;
        doc.text(lines[i],pageCenter,100+lineTop); //see this line
      }

      imgFooter.src = pdfFooterURL;
      imgFooter.onload = () => {
        doc.addImage(imgFooter, 'png', 0, 267, 210, 30);
        doc.save('cumpleaños.pdf');
      };
    };
  }
}



// WEBPACK FOOTER //
// /Proyectos-Ankaa/proyecto-meriendas-diverland/src/app/reception/monitor.component.ts